"use client";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { EditUserInfo } from "@/interface/account";
import { AppDispatch, AppState } from "@/store/configureStore";
import toast from "react-hot-toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { updateEditInfo } from "@/store/slices/userSlice";
import { EDIT_USER_INFO_API } from "@/constants";
import { useRouter } from "next/navigation";

interface EditUserHookProps {
	onClose: () => void;
	date?: string;
	setDate: React.Dispatch<React.SetStateAction<string>>;
}

const useEditUserInfo = ({ onClose, date, setDate }: EditUserHookProps) => {
	const router = useRouter();
	const user = useSelector((state: AppState) => state.user);
	const dispatch = useDispatch<AppDispatch>();

	const editInfoSchema = yup.object({
		phoneNumber: yup.string(),
		fullName: yup.string(),
		email: yup.string().email("Invalid email address"),
		dateOfBirth: yup.string(),
	});

	const {
		register,
		watch,
		handleSubmit,
		setValue,
		clearErrors,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(editInfoSchema),
		defaultValues: {
			phoneNumber: user?.phoneNumber || "",
			dateOfBirth: date || "",
		},
	});

	useEffect(() => {
		if (date) {
			setValue("dateOfBirth", date);
		}
	}, [date, setValue]);

	const handleChange = (event: any) => {
		setDate(event.target.value);
		clearErrors("dateOfBirth");
	};

	const handleEditInfo = async (data: EditUserInfo) => {
		try {
			const response = await axios.put(
				EDIT_USER_INFO_API,
				{
					fullName: data.fullName ? data.fullName : "",
					phoneNumber: data.phoneNumber ? data.phoneNumber : "",
					email: data.email ? data.email : "",
					dateOfBirth: data.dateOfBirth ? data.dateOfBirth : "",
				},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
				}
			);
			onClose();
			toast.remove();
			toast.success("Profile successfully updated");
			dispatch(
				updateEditInfo({
					fullName: response?.data?.user?.fullName,
					dateOfBirth: response?.data?.user?.dateOfBirth,
					email: response?.data?.user?.email,
					phoneNumber: response?.data?.user?.phoneNumber,
				})
			);
		} catch (error: string | any) {
			// console.log("API Error: ", error);
		}
	};

	return {
		register,
		handleSubmit,
		errors,
		watch,
		handleEditInfo,
		handleChange,
		date,
		setDate,
		setValue,
	};
};

export default useEditUserInfo;
